import { useState, useRef, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, invalidateCsrfToken } from "@/lib/queryClient";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Phone, KeyRound, ArrowLeft, CreditCard } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface CustomerAuthModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (customer: CustomerProfile) => void;
}

interface CustomerProfile {
  id: string;
  firstName: string | null;
  lastName: string | null;
  phone: string;
  email: string;
  walletBalance: string;
}

type AuthStep = "phone" | "otp" | "pin" | "access-code" | "success";

export function CustomerAuthModal({
  open,
  onOpenChange,
  onSuccess,
}: CustomerAuthModalProps) {
  const { toast } = useToast();
  const [step, setStep] = useState<AuthStep>("phone");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [requestId, setRequestId] = useState<string | null>(null);
  const [successCustomer, setSuccessCustomer] = useState<CustomerProfile | null>(null);
  const [pendingLoginToken, setPendingLoginToken] = useState<string | null>(null);
  const [pin, setPin] = useState("");

  const requestOtpMutation = useMutation({
    mutationFn: async (phoneNumber: string) => {
      const res = await apiRequest("POST", "/api/customer/auth/request-otp", {
        phone: phoneNumber,
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || "Failed to send OTP");
      }
      return res.json();
    },
    onSuccess: (data) => {
      setRequestId(data.requestId);
      setStep("otp");
      toast({
        title: "OTP Sent",
        description: "Please check your phone for the verification code",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const accessCodeMutation = useMutation({
    mutationFn: async ({ phoneNumber, code }: { phoneNumber: string; code: string }) => {
      const res = await apiRequest("POST", "/api/customer/auth/access-code", {
        phone: phoneNumber,
        code,
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || "Invalid access code");
      }
      return res.json();
    },
    onSuccess: (data) => {
      invalidateCsrfToken();
      setSuccessCustomer(data.customer);
      setStep("success");
      setTimeout(() => {
        if (data.customer) {
          onSuccess(data.customer);
          handleClose();
        }
      }, 1500);
    },
    onError: (error: Error) => {
      toast({
        title: "Sign-in Failed",
        description: error.message,
        variant: "destructive",
      });
      setOtp("");
    },
  });

  const verifyOtpMutation = useMutation({
    mutationFn: async ({ otpCode, reqId }: { otpCode: string; reqId: string }) => {
      const res = await apiRequest("POST", "/api/customer/auth/verify-otp", {
        otpCode,
        requestId: reqId,
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || "Verification failed");
      }
      return res.json();
    },
    onSuccess: (data) => {
      // PIN 2FA: OTP succeeded but customer has a PIN — collect it before session
      if (data.requiresPin && data.pendingLoginToken) {
        setPendingLoginToken(data.pendingLoginToken);
        setStep("pin");
        setOtp("");
        return;
      }
      // OTP succeeded with no PIN gate — session is established
      invalidateCsrfToken();
      setSuccessCustomer(data.customer);
      setStep("success");
      setTimeout(() => {
        if (data.customer) {
          onSuccess(data.customer);
          handleClose();
        }
      }, 1500);
    },
    onError: (error: Error) => {
      toast({
        title: "Verification Failed",
        description: error.message,
        variant: "destructive",
      });
      setOtp("");
    },
  });

  const verifyPinMutation = useMutation({
    mutationFn: async ({ token, pinValue }: { token: string; pinValue: string }) => {
      const res = await apiRequest("POST", "/api/customer/auth/verify-pin", {
        pendingLoginToken: token,
        pin: pinValue,
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || "Incorrect PIN");
      }
      return res.json();
    },
    onSuccess: (data) => {
      invalidateCsrfToken();
      setSuccessCustomer(data.customer);
      setStep("success");
      setTimeout(() => {
        if (data.customer) {
          onSuccess(data.customer);
          handleClose();
        }
      }, 1200);
    },
    onError: (error: Error) => {
      toast({
        title: "PIN Verification Failed",
        description: error.message,
        variant: "destructive",
      });
      setPin("");
    },
  });

  const handleClose = () => {
    setStep("phone");
    setPhone("");
    setOtp("");
    setPin("");
    setPendingLoginToken(null);
    setRequestId(null);
    setSuccessCustomer(null);
    onOpenChange(false);
  };

  const handlePinSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (pin.length !== 6 || !pendingLoginToken) return;
    verifyPinMutation.mutate({ token: pendingLoginToken, pinValue: pin });
  };

  const handlePhoneSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone.trim()) return;
    requestOtpMutation.mutate(phone.trim());
  };

  const handleOtpSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!otp.trim() || !requestId) return;
    verifyOtpMutation.mutate({ otpCode: otp.trim(), reqId: requestId });
  };

  const handleBack = () => {
    setStep("phone");
    setOtp("");
  };

  const handleSwitchToAccessCode = () => {
    setStep("access-code");
    setOtp("");
  };

  const isLoading = requestOtpMutation.isPending || verifyOtpMutation.isPending || accessCodeMutation.isPending || verifyPinMutation.isPending;

  const handleAccessCodeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone.trim() || otp.length !== 6) return;
    accessCodeMutation.mutate({ phoneNumber: phone.trim(), code: otp.trim() });
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {(step === "otp" || step === "access-code") && (
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 mr-1"
                onClick={handleBack}
                disabled={isLoading}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            <CreditCard className="h-5 w-5 text-primary" />
            {step === "phone"
              ? "Welcome to VedaLogic"
              : step === "otp"
              ? "Verify OTP"
              : step === "pin"
              ? "Enter Your PIN"
              : step === "access-code"
              ? "Enter Access Code"
              : "Success!"}
          </DialogTitle>
          <DialogDescription>
            {step === "phone"
              ? "Sign in or create an account with your phone number"
              : step === "otp"
              ? `Enter the 6-digit code sent to ${phone}`
              : step === "pin"
              ? "Enter your 6-digit portal PIN to finish signing in"
              : step === "access-code"
              ? "Enter the access code provided by your support contact"
              : "You're all set!"}
          </DialogDescription>
        </DialogHeader>

        {step === "phone" ? (
          <form onSubmit={handlePhoneSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+91 9876543210"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="pl-10"
                  disabled={isLoading}
                  autoFocus
                />
              </div>
              <p className="text-xs text-muted-foreground">
                New users will be automatically registered
              </p>
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={!phone.trim() || isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending OTP...
                </>
              ) : (
                "Continue"
              )}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={handleSwitchToAccessCode}
              disabled={isLoading}
            >
              Use an access code instead
            </Button>
          </form>
        ) : step === "access-code" ? (
          <form onSubmit={handleAccessCodeSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="ac-phone">Phone Number</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="ac-phone"
                  type="tel"
                  placeholder="+91 9876543210"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="pl-10"
                  disabled={isLoading}
                  autoFocus
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="ac-code">Access Code</Label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="ac-code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="Enter 6-digit code"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  className="pl-10 text-center text-lg tracking-widest"
                  disabled={isLoading}
                />
              </div>
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={!phone.trim() || otp.length !== 6 || isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Verifying...
                </>
              ) : (
                "Sign In"
              )}
            </Button>
          </form>
        ) : step === "otp" ? (
          <form onSubmit={handleOtpSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="otp">Verification Code</Label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="otp"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="Enter 6-digit code"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  className="pl-10 text-center text-lg tracking-widest"
                  disabled={isLoading}
                  autoFocus
                />
              </div>
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={otp.length !== 6 || isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Verifying...
                </>
              ) : (
                "Verify & Sign In"
              )}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={() => requestOtpMutation.mutate(phone)}
              disabled={isLoading}
            >
              Resend Code
            </Button>
          </form>
        ) : step === "pin" ? (
          <form onSubmit={handlePinSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="pin">Portal PIN</Label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="pin"
                  type="password"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="••••••"
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                  className="pl-10 text-center text-lg tracking-widest"
                  disabled={isLoading}
                  autoFocus
                />
              </div>
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={pin.length !== 6 || isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Verifying...
                </>
              ) : (
                "Sign In"
              )}
            </Button>
            <p className="text-center text-xs text-muted-foreground">
              Forgot your PIN? Contact support to reset it.
            </p>
          </form>
        ) : (
          <div className="py-8 text-center space-y-4">
            <div className="mx-auto h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
              <CreditCard className="h-8 w-8 text-primary" />
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">
                Welcome!
              </h3>
              <p className="text-muted-foreground">
                Redirecting to your dashboard...
              </p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
